import React, { lazy, Suspense, useEffect, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";

// ── Always-visible shell components — loaded eagerly ──────────────────────────
import AnnouncementBar from "./Components/AnnouncementBar/AnnouncementBar";
import Navbar          from "./Components/Navbar/Navbar";
import Footer          from "./Components/Footer/Footer";
import CookieConsent   from "./Components/CookieConsent/CookieConsent";
import ErrorBoundary   from "./Components/ErrorBoundary/ErrorBoundary";
import useScrollReveal from "./hooks/useScrollReveal";
import useLenis, { getLenis } from "./hooks/useLenis";
import { ShopContext }  from "./Context/ShopContext";
import PrefetchLink     from "./Components/PrefetchLink/PrefetchLink"; // eslint-disable-line

// ── Route-level code splitting ────────────────────────────────────────────────
// Each page is only downloaded when the user navigates to that route.
// This cuts the initial JS bundle by ~65 %.
const Shop              = lazy(() => import("./Pages/Shop"));
const ShopCategory      = lazy(() => import("./Pages/ShopCategory"));
const Product           = lazy(() => import("./Pages/Product"));
const Cart              = lazy(() => import("./Pages/Cart"));
const Checkout          = lazy(() => import("./Pages/Checkout"));
const MyOrders          = lazy(() => import("./Pages/MyOrders"));
const Account           = lazy(() => import("./Pages/Account"));
const LoginSignup       = lazy(() => import("./Pages/LoginSignup"));
const ForgotPassword    = lazy(() => import("./Pages/ForgotPassword"));
const ResetPassword     = lazy(() => import("./Pages/ResetPassword"));
// SkinAnalysis is the heaviest page (canvas + face detection) — lazy is critical
const SkinAnalysis      = lazy(() => import("./Pages/SkinAnalysis"));
const AboutUs           = lazy(() => import("./Pages/AboutUs"));
const ContactUs         = lazy(() => import("./Pages/ContactUs"));
const FAQ               = lazy(() => import("./Pages/FAQ"));
const TermsAndConditions  = lazy(() => import("./Pages/TermsAndConditions"));
const PrivacyPolicy       = lazy(() => import("./Pages/PrivacyPolicy"));
const ReturnRefundPolicy  = lazy(() => import("./Pages/ReturnRefundPolicy"));
const ShippingPolicy        = lazy(() => import("./Pages/ShippingPolicy"));
const GrievanceRedressal    = lazy(() => import("./Pages/GrievanceRedressal"));
const WhyForgePage          = lazy(() => import("./Pages/WhyForgePage"));
const Blog                  = lazy(() => import("./Pages/Blog"));
const BlogPost              = lazy(() => import("./Pages/BlogPost"));
const NotFound              = lazy(() => import("./Pages/NotFound"));

// ── Route loading fallback ────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{
    minHeight: "60vh",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#FFFFFF",
  }}>
    <span style={{
      fontFamily: "Manrope, sans-serif",
      fontSize: 11, letterSpacing: "2px", color: "#5B6B85",
    }}>
      LOADING…
    </span>
  </div>
);

// ── Wake up Render backend on first load (free tier cold-start) ───────────────
const API = process.env.REACT_APP_API_URL || "http://localhost:4000";
(function wakeBackend() {
  fetch(`${API}/allproducts`, { cache: 'no-store' }).catch(() => {});
})();

// ── Scroll & reveal on route change ──────────────────────────────────────────
function ScrollRevealWrapper() {
  const location = useLocation();
  useScrollReveal();
  useEffect(() => {
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname]);
  return null;
}


function App() {
  useLenis();
  return (
    <>
      <ErrorBoundary>
        <div>
          <Router>
            <ScrollRevealWrapper />
            <AnnouncementBar />
            <Navbar />

            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/"         element={<Shop />} />
                <Route path="/shop"     element={<ShopCategory />} />
                <Route path="/product"  element={<Product />}>
                  <Route path=":productId" element={<Product />} />
                </Route>
                <Route path="/cart"     element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders"   element={<MyOrders />} />
                <Route path="/account"  element={<Account />} />
                <Route path="/login"            element={<LoginSignup />} />
                <Route path="/forgot-password"  element={<ForgotPassword />} />
                <Route path="/reset-password"   element={<ResetPassword />} />
                <Route path="/verify-email"     element={<LoginSignup />} />
                <Route path="/skin-analysis"    element={<SkinAnalysis />} />
                <Route path="/why-forge"        element={<WhyForgePage />} />
                <Route path="/blog"             element={<Blog />} />
                <Route path="/blog/:slug"       element={<BlogPost />} />
                <Route path="/about-us"         element={<AboutUs />} />
                <Route path="/contact"          element={<ContactUs />} />
                <Route path="/faq"              element={<FAQ />} />
                <Route path="/terms-and-conditions"  element={<TermsAndConditions />} />
                <Route path="/privacy-policy"        element={<PrivacyPolicy />} />
                <Route path="/return-refund-policy"  element={<ReturnRefundPolicy />} />
          <Route path="/shipping-policy"       element={<ShippingPolicy />} />
                <Route path="/grievance-redressal"   element={<GrievanceRedressal />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>

            <Footer />
            <CookieConsent />

          </Router>
        </div>
      </ErrorBoundary>
    </>
  );
}

export default App;
